import { useEffect, useId, useMemo, useRef } from 'react';

import { company } from '~/company/Company';
import { mainStore } from '~/stores';
import StatusBarManager from '~/utils/StatusBarManager';

const SCROLL_THRESHOLD_PX = 100;

interface UseStatusBarControlOptions {
  initialColor: 'light-content' | 'dark-content';
  scrolledColor?: 'light-content' | 'dark-content';
  enabled?: boolean;
  disableScrollDetection?: boolean;
  scrollThreshold?: number;
}

/**
 * Hook for controlling React Native status bar color based on scroll position
 * Uses global StatusBarManager to handle multiple components using the hook simultaneously
 * Uses fixed scroll threshold (SCROLL_THRESHOLD_PX) for color change detection
 *
 * @param initialColor - Status bar color when at top (default position)
 * @param scrolledColor - Optional status bar color when scrolled past threshold
 * @param enabled - Whether the hook should be active (default: true)
 * @param disableScrollDetection - If true, only sets initialColor without scroll detection (default: false)
 * @param scrollThreshold - Scroll position in pixels to trigger color change (default: 100)
 *
 * @example
 * // For modals with fixed color
 * useStatusBarControl({
 *   initialColor: 'dark-content',
 *   disableScrollDetection: true,
 * });
 *
 * @example
 * // For pages with scroll-based color change
 * useStatusBarControl({
 *   initialColor: 'light-content',
 *   scrolledColor: 'dark-content',
 * });
 *
 * @example
 * // For pages with custom scroll threshold
 * useStatusBarControl({
 *   initialColor: 'light-content',
 *   scrolledColor: 'dark-content',
 *   scrollThreshold: 200,
 * });
 */
export const useStatusBarControl = ({
  initialColor,
  scrolledColor,
  enabled = true,
  disableScrollDetection = false,
  scrollThreshold = SCROLL_THRESHOLD_PX,
}: UseStatusBarControlOptions) => {
  const componentId = useId();
  const currentColorRef = useRef<string>(initialColor);
  const rafIdRef = useRef<number | null>(null);

  const isEnabled = useMemo(
    () =>
      enabled &&
      company.newDesignSystemEnabled &&
      mainStore.isRN &&
      mainStore.isRNReady,
    [
      enabled,
      company.newDesignSystemEnabled,
      mainStore.isRN,
      mainStore.isRNReady,
    ],
  );

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    let isMounted = true;
    const manager = StatusBarManager.getInstance();

    manager.push(componentId, initialColor);
    currentColorRef.current = initialColor;

    if (disableScrollDetection) {
      return () => {
        isMounted = false;
        manager.pop(componentId);
      };
    }

    if (!scrolledColor) {
      return () => {
        isMounted = false;
        manager.pop(componentId);
      };
    }

    const updateStatusBarColor = () => {
      if (!isMounted) {
        return;
      }

      const scrollY = window.scrollY;
      const shouldBeScrolledColor = scrollY >= scrollThreshold;
      const targetColor = shouldBeScrolledColor ? scrolledColor : initialColor;

      if (currentColorRef.current !== targetColor) {
        manager.update(componentId, targetColor);
        currentColorRef.current = targetColor;
      }
    };

    const handleScroll = () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      rafIdRef.current = requestAnimationFrame(updateStatusBarColor);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      isMounted = false;
      window.removeEventListener('scroll', handleScroll);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      manager.pop(componentId);
    };
  }, [
    componentId,
    initialColor,
    scrolledColor,
    disableScrollDetection,
    scrollThreshold,
    isEnabled,
  ]);
};
